import React, { useState, useRef, useEffect } from 'react';
import {
  LogOut,
  KeyRound,
  Shield,
  ChevronDown,
  X,
  Eye,
  EyeOff,
  Loader2,
  Check,
  Mail,
  Briefcase,
  MapPin,
  Camera,
  User,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { userAPI } from '../services/api';
import { useToast } from './UIProvider';
import { authedMediaUrl } from '../utils/media';
import useDialog from '../hooks/useDialog';

// ─── Avatar helper ──────────────────────────────────────────────────────

const Avatar = ({ user, size = 40, className = '', showUpload = false, onUpload, uploading }) => {
  const fileRef = useRef(null);
  const px = `${size}px`;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const imageUrl = authedMediaUrl(user.profileImageUrl);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUpload) onUpload(file);
    e.target.value = '';
  };

  return (
    <div className={`relative group ${className}`} style={{ width: px, height: px }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={user.fullName}
          className="w-full h-full rounded-full object-cover border-2 border-white shadow-sm"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-navy-700 to-navy-900 rounded-full flex items-center justify-center shadow-sm border-2 border-white">
          <span className="text-white font-bold" style={{ fontSize: size * 0.35 }}>
            {getInitials(user.fullName)}
          </span>
        </div>
      )}

      {showUpload && (
        <>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="Change profile photo"
            className="group/upload absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 focus-visible:bg-black/40 flex items-center justify-center transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 group-focus-visible/upload:opacity-100 transition-opacity duration-200" />
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

// ─── ProfileMenu ────────────────────────────────────────────────────────

// The two colour themes, shown with the frame and action colours each really uses.
const COLOR_THEME_SWATCHES = [
  { value: 'blue', label: 'Blue', colors: ['#003078', '#1660b8', '#00a0c4'] },
  { value: 'green', label: 'Green', colors: ['#065f46', '#059669', '#14b8a6'] },
];

const ProfileMenu = ({ authUser, onLogout, onAuthUserUpdate, theme, onThemeChange, colorTheme, onColorThemeChange }) => {
  const showToast = useToast();
  const [open, setOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const result = await userAPI.uploadProfileImage(authUser.id, file);
      // Update auth user with new image URL
      const updatedUser = { ...authUser, profileImageUrl: result.imageUrl };
      localStorage.setItem('clinic_auth_user', JSON.stringify(updatedUser));
      if (onAuthUserUpdate) onAuthUserUpdate(updatedUser);
    } catch (err) {
      showToast('error', err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      ADMIN: 'Administrator',
      ACCOUNTS: 'Accounts',
      DOCTOR: 'Doctor',
      NURSE: 'Nurse',
      PHARMACIST: 'Pharmacist',
      DISPENSER: 'Dispenser',
      RECEPTIONIST: 'Receptionist',
      TECHNICIAN: 'Technician',
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      ADMIN: 'bg-purple-100 text-purple-700',
      ACCOUNTS: 'bg-indigo-100 text-indigo-700',
      DOCTOR: 'bg-emerald-100 text-emerald-700',
      NURSE: 'bg-pink-100 text-pink-700',
      PHARMACIST: 'bg-blue-100 text-blue-700',
      DISPENSER: 'bg-purple-100 text-purple-700',
      RECEPTIONIST: 'bg-amber-100 text-amber-700',
      TECHNICIAN: 'bg-teal-100 text-teal-700',
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        {/* Trigger button */}
        <button
          onClick={() => setOpen(!open)}
          className={`flex items-center space-x-3 px-3 py-2 rounded-xl transition-all duration-200 ${
            open ? 'bg-emerald-50 ring-2 ring-emerald-200' : 'hover:bg-gray-100'
          }`}
        >
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-gray-900">{authUser.fullName}</p>
            <p className="text-xs text-gray-500">{getRoleLabel(authUser.role)}</p>
          </div>
          <Avatar user={authUser} size={40} />
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50 animate-in">
            <style>{`
              .animate-in {
                animation: dropIn 0.2s ease-out;
              }
              @keyframes dropIn {
                from { opacity: 0; transform: translateY(-8px) scale(0.96); }
                to { opacity: 1; transform: translateY(0) scale(1); }
              }
            `}</style>

            {/* Profile header */}
            <div className="bg-gradient-to-br from-navy-700 to-navy-900 px-5 py-5">
              <div className="flex items-center space-x-4">
                <Avatar
                  user={authUser}
                  size={56}
                  showUpload
                  onUpload={handleUpload}
                  uploading={uploading}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-base truncate">
                    {authUser.fullName}
                  </p>
                  <p className="text-emerald-100 text-sm truncate">@{authUser.username}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm">
                  <Shield className="w-3 h-3 mr-1" />
                  {getRoleLabel(authUser.role)}
                </span>
                {!authUser.profileImageUrl && (
                  <span className="text-emerald-200 text-xs">Hover avatar to upload photo</span>
                )}
              </div>
            </div>

            {/* Profile details */}
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Account Details
              </p>
              <div className="space-y-2.5">
                <div className="flex items-center space-x-3 text-sm">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-500 w-16 flex-shrink-0">Name</span>
                  <span className="text-gray-900 font-medium truncate">{authUser.fullName}</span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-500 w-16 flex-shrink-0">Role</span>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${getRoleBadgeColor(authUser.role)}`}>
                    {getRoleLabel(authUser.role)}
                  </span>
                </div>
                {authUser.email && (
                  <div className="flex items-center space-x-3 text-sm">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-500 w-16 flex-shrink-0">Email</span>
                    <span className="text-gray-900 font-medium truncate">{authUser.email}</span>
                  </div>
                )}
                <div className="flex items-center space-x-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-500 w-16 flex-shrink-0">Hospital</span>
                  <span className="text-gray-900 font-medium truncate">PAMH</span>
                </div>
              </div>
            </div>

            {/* Theme toggle */}
            <div className="px-5 py-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Appearance
              </p>
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[
                  { value: 'light', icon: Sun, label: 'Light' },
                  { value: 'dark', icon: Moon, label: 'Dark' },
                  { value: 'auto', icon: Monitor, label: 'Auto' },
                ].map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onThemeChange && onThemeChange(value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                      theme === value
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Colour theme: the frame, buttons and highlights in the logo's blues or in green.
                  Each swatch shows that theme's frame and action colours as they really are, so
                  they are written as fixed colours rather than through the theme variables. */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-3 mb-2">
                Colour theme
              </p>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Colour theme">
                {COLOR_THEME_SWATCHES.map((t) => {
                  const on = colorTheme === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => onColorThemeChange && onColorThemeChange(t.value)}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs font-medium transition-colors ${
                        on ? 'border-gray-900 bg-gray-50 text-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex -space-x-1" aria-hidden="true">
                        {t.colors.map((c) => (
                          <span key={c} className="w-4 h-4 rounded-full ring-2 ring-white" style={{ background: c }} />
                        ))}
                      </span>
                      {t.label}
                      {on && <Check className="w-3.5 h-3.5 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="p-2">
              <button
                onClick={() => {
                  setOpen(false);
                  setShowPasswordModal(true);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <KeyRound className="w-4 h-4 text-gray-400" />
                <span className="font-medium">Change Password</span>
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  onLogout();
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <ChangePasswordModal
          userId={authUser.id}
          onClose={() => setShowPasswordModal(false)}
        />
      )}
    </>
  );
};

// ─── Change Password Modal ──────────────────────────────────────────────

const ChangePasswordModal = ({ userId, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const passwordDialogRef = useDialog(onClose);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      // Signs this account out on every other device; the API layer keeps this tab signed in.
      await userAPI.changePassword(userId, currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={passwordDialogRef} className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        style={{ animation: 'dropIn 0.25s ease-out' }}
      >
        <style>{`
          @keyframes dropIn {
            from { opacity: 0; transform: translateY(-8px) scale(0.96); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Change Password</h3>
              <p className="text-xs text-gray-500">Update your account password</p>
            </div>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success state */}
        {success ? (
          <div className="px-6 py-10 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-1">Password Changed</h4>
            <p className="text-sm text-gray-500">Your password has been updated successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Error */}
            {error && (
              <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                <span className="text-red-500 text-sm font-bold mt-0.5">!</span>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Current Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  autoFocus
                  className="block w-full px-4 pr-11 py-3 border-2 border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-0 focus:border-emerald-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="block w-full px-4 pr-11 py-3 border-2 border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-0 focus:border-emerald-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {newPassword && (
                <div className="mt-2 flex items-center space-x-2">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        newPassword.length >= 12
                          ? 'w-full bg-emerald-500'
                          : newPassword.length >= 8
                          ? 'w-2/3 bg-amber-500'
                          : 'w-1/3 bg-red-500'
                      }`}
                    />
                  </div>
                  <span className={`text-xs font-medium ${
                    newPassword.length >= 12
                      ? 'text-emerald-600'
                      : newPassword.length >= 8
                      ? 'text-amber-600'
                      : 'text-red-500'
                  }`}>
                    {newPassword.length >= 12 ? 'Strong' : newPassword.length >= 8 ? 'Good' : 'Weak'}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className={`block w-full px-4 py-3 border-2 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-0 transition-colors ${
                  confirmPassword && confirmPassword !== newPassword
                    ? 'border-red-300 focus:border-red-500'
                    : confirmPassword && confirmPassword === newPassword
                    ? 'border-emerald-300 focus:border-emerald-500'
                    : 'border-gray-200 focus:border-emerald-500'
                }`}
              />
              {confirmPassword && confirmPassword !== newPassword && (
                <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl hover:from-emerald-700 hover:to-emerald-800 shadow-sm hover:shadow transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfileMenu;
